import type {CopNetworkObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/CopNetwork';
import type {CopNetworkMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/CopNetwork';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {EventsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {SRGBColorSpace} from 'three';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([-1.6846618089891994, 0.6698293049902135, -9.963447217276514]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return cameraOrbitControls1;
			}
			create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return events1;
		}
		create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([6.1083911586397095, 3.617256485043771, -9.63946941516725]);
		perspectiveCamera1.p.r.set([-83.72729559289854, 69.16867648840278, 83.29244801887643]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return perspectiveCamera1;
	}
	function create_COP(parentNode: RootManagerNode) {
		var COP = parentNode.createNode('copNetwork');
		COP.setName('COP');
		function create_envMap(COP: CopNetworkObjNode) {
			var envMap = COP.createNode('envMap');
			envMap.setName('envMap');
			envMap.uiData.setPosition(50, 200);
			envMap.params.postCreateSpareParams();
			envMap.params.runOnSceneLoadHooks();
			return envMap;
		}
		function create_imageEnv(COP: CopNetworkObjNode) {
			var imageEnv = COP.createNode('imageEXR');
			imageEnv.setName('imageEnv');
			imageEnv.uiData.setPosition(50, 100);
			imageEnv.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/piz_compressed.exr'
			);
			imageEnv.params.postCreateSpareParams();
			imageEnv.params.runOnSceneLoadHooks();
			return imageEnv;
		}
		function create_imageUv(COP: CopNetworkObjNode) {
			var imageUv = COP.createNode('image');
			imageUv.setName('imageUv');
			imageUv.uiData.setPosition(-100, 100);
			imageUv.p.url.set('https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/uv.jpg');
			imageUv.p.tflipY.set(true);
			imageUv.params.postCreateSpareParams();
			imageUv.params.runOnSceneLoadHooks();
			return imageUv;
		}
		var envMap = create_envMap(COP);
		var imageEnv = create_imageEnv(COP);
		create_imageUv(COP);
		envMap.setInput(0, imageEnv);
		COP.uiData.setPosition(-200, 200);
		COP.params.postCreateSpareParams();
		COP.params.runOnSceneLoadHooks();
		return COP;
	}
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_COP_marble(MAT: MaterialsNetworkSopNode) {
				var COP_marble = MAT.createNode('copNetwork');
				COP_marble.setName('COP_marble');
				function create_image_MARBLE_DIFFUSE(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_DIFFUSE = COP_marble.createNode('image');
					image_MARBLE_DIFFUSE.setName('image_MARBLE_DIFFUSE');
					image_MARBLE_DIFFUSE.uiData.setPosition(-300, -150);
					image_MARBLE_DIFFUSE.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/diffuse.jpg'
					);
					image_MARBLE_DIFFUSE.p.tcolorSpace.set(true);
					image_MARBLE_DIFFUSE.p.colorSpace.set(SRGBColorSpace);
					image_MARBLE_DIFFUSE.p.tminFilter.set(true);
					image_MARBLE_DIFFUSE.p.tmagFilter.set(true);
					image_MARBLE_DIFFUSE.p.tanisotropy.set(true);
					image_MARBLE_DIFFUSE.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_DIFFUSE.params.postCreateSpareParams();
					image_MARBLE_DIFFUSE.params.runOnSceneLoadHooks();
					return image_MARBLE_DIFFUSE;
				}
				function create_image_MARBLE_DISP(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_DISP = COP_marble.createNode('image');
					image_MARBLE_DISP.setName('image_MARBLE_DISP');
					image_MARBLE_DISP.uiData.setPosition(-300, 50);
					image_MARBLE_DISP.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/displacement.png'
					);
					image_MARBLE_DISP.p.tminFilter.set(true);
					image_MARBLE_DISP.p.tmagFilter.set(true);
					image_MARBLE_DISP.p.tanisotropy.set(true);
					image_MARBLE_DISP.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_DISP.params.postCreateSpareParams();
					image_MARBLE_DISP.params.runOnSceneLoadHooks();
					return image_MARBLE_DISP;
				}
				function create_image_MARBLE_ROUGHNESS(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_ROUGHNESS = COP_marble.createNode('image');
					image_MARBLE_ROUGHNESS.setName('image_MARBLE_ROUGHNESS');
					image_MARBLE_ROUGHNESS.uiData.setPosition(-300, 250);
					image_MARBLE_ROUGHNESS.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/rough.jpg'
					);
					image_MARBLE_ROUGHNESS.p.tminFilter.set(true);
					image_MARBLE_ROUGHNESS.p.tmagFilter.set(true);
					image_MARBLE_ROUGHNESS.p.tanisotropy.set(true);
					image_MARBLE_ROUGHNESS.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_ROUGHNESS.params.postCreateSpareParams();
					image_MARBLE_ROUGHNESS.params.runOnSceneLoadHooks();
					return image_MARBLE_ROUGHNESS;
				}
				create_image_MARBLE_DIFFUSE(COP_marble);
				create_image_MARBLE_DISP(COP_marble);
				create_image_MARBLE_ROUGHNESS(COP_marble);
				COP_marble.uiData.setPosition(300, 600);
				COP_marble.params.postCreateSpareParams();
				COP_marble.params.runOnSceneLoadHooks();
				return COP_marble;
			}
			function create_COP_medieval_block(MAT: MaterialsNetworkSopNode) {
				var COP_medieval_block = MAT.createNode('copNetwork');
				COP_medieval_block.setName('COP_medieval_block');
				function create_image_MEDIEVAL_BLOCK_DIFFUSE(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_DIFFUSE = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_DIFFUSE.setName('image_MEDIEVAL_BLOCK_DIFFUSE');
					image_MEDIEVAL_BLOCK_DIFFUSE.uiData.setPosition(-300, -150);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/diffuse.jpg'
					);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tcolorSpace.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.colorSpace.set(SRGBColorSpace);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_DIFFUSE.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_DIFFUSE;
				}
				function create_image_MEDIEVAL_BLOCK_DISP(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_DISP = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_DISP.setName('image_MEDIEVAL_BLOCK_DISP');
					image_MEDIEVAL_BLOCK_DISP.uiData.setPosition(-300, 50);
					image_MEDIEVAL_BLOCK_DISP.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/displacement.png'
					);
					image_MEDIEVAL_BLOCK_DISP.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_DISP.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_DISP.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_DISP;
				}
				function create_image_MEDIEVAL_BLOCK_ROUGHNESS(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_ROUGHNESS = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_ROUGHNESS.setName('image_MEDIEVAL_BLOCK_ROUGHNESS');
					image_MEDIEVAL_BLOCK_ROUGHNESS.uiData.setPosition(-300, 250);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/rough.jpg'
					);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_ROUGHNESS.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_ROUGHNESS;
				}
				create_image_MEDIEVAL_BLOCK_DIFFUSE(COP_medieval_block);
				create_image_MEDIEVAL_BLOCK_DISP(COP_medieval_block);
				create_image_MEDIEVAL_BLOCK_ROUGHNESS(COP_medieval_block);
				COP_medieval_block.uiData.setPosition(300, 800);
				COP_medieval_block.params.postCreateSpareParams();
				COP_medieval_block.params.runOnSceneLoadHooks();
				return COP_medieval_block;
			}
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				meshStandard1.uiData.setPosition(0, 0);
				meshStandard1.p.useMap.set(true);
				meshStandard1.p.map.set('../../../COP/imageUv');
				meshStandard1.p.useEnvMap.set(true);
				meshStandard1.p.envMap.set('../../../COP/envMap');
				meshStandard1.p.metalness.set(1);
				meshStandard1.p.roughness.set(0.07);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return meshStandard1;
			}
			function create_meshStandard2(MAT: MaterialsNetworkSopNode) {
				var meshStandard2 = MAT.createNode('meshPhysical');
				meshStandard2.setName('meshStandard2');
				meshStandard2.uiData.setPosition(0, 200);
				meshStandard2.p.useMap.set(true);
				meshStandard2.p.map.set('../../../COP/imageUv');
				meshStandard2.p.useEnvMap.set(true);
				meshStandard2.p.envMap.set('../../../COP/envMap');
				meshStandard2.p.metalness.set(1);
				meshStandard2.p.roughness.set(0.34);
				meshStandard2.params.postCreateSpareParams();
				meshStandard2.params.runOnSceneLoadHooks();
				return meshStandard2;
			}
			function create_meshStandard3(MAT: MaterialsNetworkSopNode) {
				var meshStandard3 = MAT.createNode('meshPhysical');
				meshStandard3.setName('meshStandard3');
				meshStandard3.uiData.setPosition(0, 400);
				meshStandard3.p.useMap.set(true);
				meshStandard3.p.map.set('../../../COP/imageUv');
				meshStandard3.p.useEnvMap.set(true);
				meshStandard3.p.envMap.set('../../../COP/envMap');
				meshStandard3.params.postCreateSpareParams();
				meshStandard3.params.runOnSceneLoadHooks();
				return meshStandard3;
			}
			function create_meshStandard4(MAT: MaterialsNetworkSopNode) {
				var meshStandard4 = MAT.createNode('meshPhysical');
				meshStandard4.setName('meshStandard4');
				meshStandard4.uiData.setPosition(0, 600);
				meshStandard4.p.useMap.set(true);
				meshStandard4.p.map.set('../COP_marble/image_MARBLE_DIFFUSE');
				meshStandard4.p.useDisplacementMap.set(true);
				meshStandard4.p.displacementMap.set('../COP_marble/image_MARBLE_DISP');
				meshStandard4.p.displacementScale.set(0.03);
				meshStandard4.p.useEnvMap.set(true);
				meshStandard4.p.envMap.set('../../../COP/envMap');
				meshStandard4.p.useRoughnessMap.set(true);
				meshStandard4.p.roughnessMap.set('../COP_marble/image_MARBLE_ROUGHNESS');
				meshStandard4.params.postCreateSpareParams();
				meshStandard4.params.runOnSceneLoadHooks();
				return meshStandard4;
			}
			function create_meshStandard5(MAT: MaterialsNetworkSopNode) {
				var meshStandard5 = MAT.createNode('meshPhysical');
				meshStandard5.setName('meshStandard5');
				meshStandard5.uiData.setPosition(0, 800);
				meshStandard5.p.useMap.set(true);
				meshStandard5.p.map.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_DIFFUSE');
				meshStandard5.p.useDisplacementMap.set(true);
				meshStandard5.p.displacementMap.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_DISP');
				meshStandard5.p.displacementScale.set(0.25);
				meshStandard5.p.useEnvMap.set(true);
				meshStandard5.p.envMap.set('../../../COP/envMap');
				meshStandard5.p.useRoughnessMap.set(true);
				meshStandard5.p.roughnessMap.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_ROUGHNESS');
				meshStandard5.params.postCreateSpareParams();
				meshStandard5.params.runOnSceneLoadHooks();
				return meshStandard5;
			}
			function create_meshStandard6(MAT: MaterialsNetworkSopNode) {
				var meshStandard6 = MAT.createNode('meshPhysical');
				meshStandard6.setName('meshStandard6');
				meshStandard6.uiData.setComment(
					'the MeshPhysicalMaterial is similar to the MeshStandardMaterial, with a few additional properties such as:- transmission- clearcoat - sheen'
				);
				meshStandard6.uiData.setPosition(0, 1050);
				meshStandard6.p.useMap.set(true);
				meshStandard6.p.map.set('../../../COP/imageUv');
				meshStandard6.p.useEnvMap.set(true);
				meshStandard6.p.envMap.set('../../../COP/envMap');
				meshStandard6.p.roughness.set(0);
				meshStandard6.p.transmission.set(1);
				meshStandard6.params.postCreateSpareParams();
				meshStandard6.params.runOnSceneLoadHooks();
				return meshStandard6;
			}
			function create_meshStandard7(MAT: MaterialsNetworkSopNode) {
				var meshStandard7 = MAT.createNode('meshPhysical');
				meshStandard7.setName('meshStandard7');
				meshStandard7.uiData.setPosition(0, 1450);
				meshStandard7.p.useMap.set(true);
				meshStandard7.p.map.set('../../../COP/imageUv');
				meshStandard7.p.useEnvMap.set(true);
				meshStandard7.p.envMap.set('../../../COP/envMap');
				meshStandard7.p.roughness.set(0);
				meshStandard7.p.transmission.set(1);
				meshStandard7.p.ior.set(1.626651);
				meshStandard7.p.thickness.set(1.1);
				meshStandard7.params.postCreateSpareParams();
				meshStandard7.params.runOnSceneLoadHooks();
				return meshStandard7;
			}
			function create_meshStandard8(MAT: MaterialsNetworkSopNode) {
				var meshStandard8 = MAT.createNode('meshPhysical');
				meshStandard8.setName('meshStandard8');
				meshStandard8.uiData.setPosition(0, 1750);
				meshStandard8.p.map.set('../../../COP/imageUv');
				meshStandard8.p.useEnvMap.set(true);
				meshStandard8.p.envMap.set('../../../COP/envMap');
				meshStandard8.p.roughness.set(0.32);
				meshStandard8.p.transmission.set(1);
				meshStandard8.p.ior.set(1.626651);
				meshStandard8.p.thickness.set(1.1);
				meshStandard8.p.attenuationDistance.set(3.9);
				meshStandard8.p.attenuationColor.set([0.32941176470588235, 0.13333333333333333, 0.8666666666666667]);
				meshStandard8.params.postCreateSpareParams();
				meshStandard8.params.runOnSceneLoadHooks();
				return meshStandard8;
			}
			create_COP_marble(MAT);
			create_COP_medieval_block(MAT);
			create_meshStandard1(MAT);
			create_meshStandard2(MAT);
			create_meshStandard3(MAT);
			create_meshStandard4(MAT);
			create_meshStandard5(MAT);
			create_meshStandard6(MAT);
			create_meshStandard7(MAT);
			create_meshStandard8(MAT);
			MAT.uiData.setPosition(-300, 150);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(-50, 150);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		function create_material2(geo1: GeoObjNode) {
			var material2 = geo1.createNode('material');
			material2.setName('material2');
			material2.uiData.setPosition(150, 150);
			material2.p.material.set('../MAT/meshStandard2');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return material2;
		}
		function create_material3(geo1: GeoObjNode) {
			var material3 = geo1.createNode('material');
			material3.setName('material3');
			material3.uiData.setPosition(350, 150);
			material3.p.material.set('../MAT/meshStandard3');
			material3.params.postCreateSpareParams();
			material3.params.runOnSceneLoadHooks();
			return material3;
		}
		function create_material4(geo1: GeoObjNode) {
			var material4 = geo1.createNode('material');
			material4.setName('material4');
			material4.uiData.setPosition(600, 150);
			material4.p.material.set('../MAT/meshStandard4');
			material4.params.postCreateSpareParams();
			material4.params.runOnSceneLoadHooks();
			return material4;
		}
		function create_material5(geo1: GeoObjNode) {
			var material5 = geo1.createNode('material');
			material5.setName('material5');
			material5.uiData.setPosition(800, 150);
			material5.p.material.set('../MAT/meshStandard5');
			material5.params.postCreateSpareParams();
			material5.params.runOnSceneLoadHooks();
			return material5;
		}
		function create_material6(geo1: GeoObjNode) {
			var material6 = geo1.createNode('material');
			material6.setName('material6');
			material6.uiData.setPosition(1000, 150);
			material6.p.material.set('../MAT/meshStandard6');
			material6.params.postCreateSpareParams();
			material6.params.runOnSceneLoadHooks();
			return material6;
		}
		function create_material7(geo1: GeoObjNode) {
			var material7 = geo1.createNode('material');
			material7.setName('material7');
			material7.uiData.setPosition(1250, 150);
			material7.p.material.set('../MAT/meshStandard7');
			material7.params.postCreateSpareParams();
			material7.params.runOnSceneLoadHooks();
			return material7;
		}
		function create_material8(geo1: GeoObjNode) {
			var material8 = geo1.createNode('material');
			material8.setName('material8');
			material8.uiData.setPosition(1500, 150);
			material8.p.material.set('../MAT/meshStandard8');
			material8.params.postCreateSpareParams();
			material8.params.runOnSceneLoadHooks();
			return material8;
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(550, 600);
			merge1.flags.display.set(true);
			merge1.p.inputsCount.set(8);
			merge1.io.inputs.setCount(1, 8);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_sphere1(geo1: GeoObjNode) {
			var sphere1 = geo1.createNode('sphere');
			sphere1.setName('sphere1');
			sphere1.uiData.setPosition(350, -300);
			sphere1.p.radius.set(0.92);
			sphere1.p.resolution.set([60, 60]);
			sphere1.params.postCreateSpareParams();
			sphere1.params.runOnSceneLoadHooks();
			return sphere1;
		}
		function create_transform1(geo1: GeoObjNode) {
			var transform1 = geo1.createNode('transform');
			transform1.setName('transform1');
			transform1.uiData.setPosition(-50, 0);
			transform1.p.t.set([0, 1.0741628078914935, 2]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return transform1;
		}
		function create_transform2(geo1: GeoObjNode) {
			var transform2 = geo1.createNode('transform');
			transform2.setName('transform2');
			transform2.uiData.setPosition(150, 0);
			transform2.p.t.set([0, 1.0741628078914935, 0]);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return transform2;
		}
		function create_transform3(geo1: GeoObjNode) {
			var transform3 = geo1.createNode('transform');
			transform3.setName('transform3');
			transform3.uiData.setPosition(350, 0);
			transform3.p.t.set([0, 1.0741628078914935, -2]);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return transform3;
		}
		function create_transform4(geo1: GeoObjNode) {
			var transform4 = geo1.createNode('transform');
			transform4.setName('transform4');
			transform4.uiData.setPosition(600, 0);
			transform4.p.t.set([0, 1.0741628078914935, -4]);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return transform4;
		}
		function create_transform5(geo1: GeoObjNode) {
			var transform5 = geo1.createNode('transform');
			transform5.setName('transform5');
			transform5.uiData.setPosition(800, 0);
			transform5.p.t.set([0, 1.0741628078914935, -6]);
			transform5.params.postCreateSpareParams();
			transform5.params.runOnSceneLoadHooks();
			return transform5;
		}
		function create_transform6(geo1: GeoObjNode) {
			var transform6 = geo1.createNode('transform');
			transform6.setName('transform6');
			transform6.uiData.setPosition(1000, 0);
			transform6.p.t.set([0, 1.0741628078914935, -8]);
			transform6.params.postCreateSpareParams();
			transform6.params.runOnSceneLoadHooks();
			return transform6;
		}
		function create_transform7(geo1: GeoObjNode) {
			var transform7 = geo1.createNode('transform');
			transform7.setName('transform7');
			transform7.uiData.setPosition(1250, 0);
			transform7.p.t.set([0, 1.0741628078914935, -10]);
			transform7.params.postCreateSpareParams();
			transform7.params.runOnSceneLoadHooks();
			return transform7;
		}
		function create_transform8(geo1: GeoObjNode) {
			var transform8 = geo1.createNode('transform');
			transform8.setName('transform8');
			transform8.uiData.setPosition(1500, 0);
			transform8.p.t.set([0, 1.0741628078914935, -12]);
			transform8.params.postCreateSpareParams();
			transform8.params.runOnSceneLoadHooks();
			return transform8;
		}
		create_MAT(geo1);
		var material1 = create_material1(geo1);
		var material2 = create_material2(geo1);
		var material3 = create_material3(geo1);
		var material4 = create_material4(geo1);
		var material5 = create_material5(geo1);
		var material6 = create_material6(geo1);
		var material7 = create_material7(geo1);
		var material8 = create_material8(geo1);
		var merge1 = create_merge1(geo1);
		var sphere1 = create_sphere1(geo1);
		var transform1 = create_transform1(geo1);
		var transform2 = create_transform2(geo1);
		var transform3 = create_transform3(geo1);
		var transform4 = create_transform4(geo1);
		var transform5 = create_transform5(geo1);
		var transform6 = create_transform6(geo1);
		var transform7 = create_transform7(geo1);
		var transform8 = create_transform8(geo1);
		material1.setInput(0, transform1);
		material2.setInput(0, transform2);
		material3.setInput(0, transform3);
		material4.setInput(0, transform4);
		material5.setInput(0, transform5);
		material6.setInput(0, transform6);
		material7.setInput(0, transform7);
		material8.setInput(0, transform8);
		merge1.setInput(0, material1);
		merge1.setInput(1, material2);
		merge1.setInput(2, material3);
		merge1.setInput(3, material4);
		merge1.setInput(4, material5);
		merge1.setInput(5, material6);
		merge1.setInput(6, material7);
		merge1.setInput(7, material8);
		transform1.setInput(0, sphere1);
		transform2.setInput(0, sphere1);
		transform3.setInput(0, sphere1);
		transform4.setInput(0, sphere1);
		transform5.setInput(0, sphere1);
		transform6.setInput(0, sphere1);
		transform7.setInput(0, sphere1);
		transform8.setInput(0, sphere1);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return geo1;
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			hemisphereLight1.uiData.setPosition(-100, 0);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return hemisphereLight1;
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(50, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			polarTransform1.uiData.setPosition(150, 50);
			polarTransform1.p.longitude.set(7.2);
			polarTransform1.p.latitude.set(46.8);
			polarTransform1.p.depth.set(5.8);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return polarTransform1;
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			spotLight1.uiData.setPosition(150, -150);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return spotLight1;
		}
		var hemisphereLight1 = create_hemisphereLight1(lights);
		var merge1 = create_merge1(lights);
		var polarTransform1 = create_polarTransform1(lights);
		var spotLight1 = create_spotLight1(lights);
		merge1.setInput(0, hemisphereLight1);
		merge1.setInput(1, polarTransform1);
		polarTransform1.setInput(0, spotLight1);
		lights.uiData.setPosition(-50, -50);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return lights;
	}
	function create_ground(parentNode: RootManagerNode) {
		var ground = parentNode.createNode('geo');
		ground.setName('ground');
		function create_MAT(ground: GeoObjNode) {
			var MAT = ground.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_COP(MAT: MaterialsNetworkSopNode) {
				var COP = MAT.createNode('copNetwork');
				COP.setName('COP');
				function create_image_DIFF(COP: CopNetworkMatNode) {
					var image_DIFF = COP.createNode('image');
					image_DIFF.setName('image_DIFF');
					image_DIFF.uiData.setPosition(0, 0);
					image_DIFF.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/floor_tiles_06/2k/diffuse.jpg'
					);
					image_DIFF.p.tcolorSpace.set(true);
					image_DIFF.p.colorSpace.set(SRGBColorSpace);
					image_DIFF.p.tminFilter.set(true);
					image_DIFF.p.tmagFilter.set(true);
					image_DIFF.p.tanisotropy.set(true);
					image_DIFF.p.useRendererMaxAnisotropy.set(true);
					image_DIFF.params.postCreateSpareParams();
					image_DIFF.params.runOnSceneLoadHooks();
					return image_DIFF;
				}
				function create_image_ROUGHNESS(COP: CopNetworkMatNode) {
					var image_ROUGHNESS = COP.createNode('image');
					image_ROUGHNESS.setName('image_ROUGHNESS');
					image_ROUGHNESS.uiData.setPosition(0, 200);
					image_ROUGHNESS.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/floor_tiles_06/2k/rough.jpg'
					);
					image_ROUGHNESS.p.tminFilter.set(true);
					image_ROUGHNESS.p.tmagFilter.set(true);
					image_ROUGHNESS.p.tanisotropy.set(true);
					image_ROUGHNESS.p.useRendererMaxAnisotropy.set(true);
					image_ROUGHNESS.params.postCreateSpareParams();
					image_ROUGHNESS.params.runOnSceneLoadHooks();
					return image_ROUGHNESS;
				}
				create_image_DIFF(COP);
				create_image_ROUGHNESS(COP);
				COP.uiData.setPosition(300, 0);
				COP.params.postCreateSpareParams();
				COP.params.runOnSceneLoadHooks();
				return COP;
			}
			function create_meshPhysical1(MAT: MaterialsNetworkSopNode) {
				var meshPhysical1 = MAT.createNode('meshPhysical');
				meshPhysical1.setName('meshPhysical1');
				meshPhysical1.uiData.setPosition(0, 0);
				meshPhysical1.p.useMap.set(true);
				meshPhysical1.p.map.set('../COP/image_DIFF');
				meshPhysical1.p.useRoughnessMap.set(true);
				meshPhysical1.p.roughnessMap.set('../COP/image_ROUGHNESS');
				meshPhysical1.params.postCreateSpareParams();
				meshPhysical1.params.runOnSceneLoadHooks();
				return meshPhysical1;
			}
			create_COP(MAT);
			create_meshPhysical1(MAT);
			MAT.uiData.setPosition(-100, 150);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_material1(ground: GeoObjNode) {
			var material1 = ground.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(100, 150);
			material1.p.material.set('../MAT/meshPhysical1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		function create_plane1(ground: GeoObjNode) {
			var plane1 = ground.createNode('plane');
			plane1.setName('plane1');
			plane1.uiData.setPosition(100, 0);
			plane1.p.size.set([25, 25]);
			plane1.params.postCreateSpareParams();
			plane1.params.runOnSceneLoadHooks();
			return plane1;
		}
		function create_uvTransform1(ground: GeoObjNode) {
			var uvTransform1 = ground.createNode('uvTransform');
			uvTransform1.setName('uvTransform1');
			uvTransform1.uiData.setPosition(100, 250);
			uvTransform1.flags.display.set(true);
			uvTransform1.p.s.set([2.3000000000000003, 2.3000000000000003]);
			uvTransform1.params.postCreateSpareParams();
			uvTransform1.params.runOnSceneLoadHooks();
			return uvTransform1;
		}
		create_MAT(ground);
		var material1 = create_material1(ground);
		var plane1 = create_plane1(ground);
		var uvTransform1 = create_uvTransform1(ground);
		material1.setInput(0, plane1);
		uvTransform1.setInput(0, material1);
		ground.uiData.setPosition(-50, -250);
		ground.flags.display.set(true);
		ground.p.t.set([0, 0, -3.958229732582667]);
		ground.params.postCreateSpareParams();
		ground.params.runOnSceneLoadHooks();
		return ground;
	}
	var perspectiveCamera1 = create_perspectiveCamera1(root);
	create_COP(root);
	var geo1 = create_geo1(root);
	create_lights(root);
	create_ground(root);

	return {
		scene,
		cameraPath: perspectiveCamera1.path(),
		focusedNodes: [geo1.nodesByType('material')[0]],
		autoLayout: false,
	};
}
// export const matMeshPhysical = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/mat/meshPhysical`,
// 		createData,
// 		relatedNodes: [MeshPhysicalMatNode],
// 	}),
// ];
